import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { GetAllTenants, Tenant } from "../Services/APIService";
import { useNavigate, useLocation } from "react-router-dom";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import CreateTenantModal from "../Components/CreateTenantModal";
import EditTenantModal from "../Components/EditTenantModal";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";

const TenantTable: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Tenant[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [editTenantData, setEditTenantData] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const handleLocationState = async () => {
      setLoading(true);
      try {
        const menuID = location.state?.menuID;
        const tenantDataUpdated = location.state?.tenantDataUpdated;
        console.log("Location state menu id:", menuID);
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }

        if (tenantDataUpdated) {
          toast.success("Tenant updated successfully");
          fetchTenants();
          const { tenantDataUpdated: _, ...newState } = location.state;
          navigate(location.pathname, {
            state: newState,
            replace: true,
          });
        }
      } catch (error) {
        console.error("Error handling location state:", error);
      } finally {
        setLoading(false);
      }
    };

    handleLocationState();
  }, [location.state]);

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const subMenuId = location.state?.subMenuId;
        const userDataUpdated = location.state?.userDataUpdated; // Add this

        const previousMenuID = localStorage.getItem("activeMenuID");
        console.log("MenuID from location state:", menuID);
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  const fetchTenants = async (): Promise<void> => {
    try {
      setLoading(true);
      const tenantData = await GetAllTenants();
      setTenants(tenantData);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  const handleEdit = (tenant: Tenant) => {
    setEditTenantData(tenant);
    setIsEditModalOpen(true);
  };

  const handleRowSelected = (state: { selectedRows: Tenant[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const handleCreateSubmit = () => {
    setIsCreateModalOpen(false);
    setTimeout(() => {
      navigate("/TenantTable", { state: { tenantDataUpdated: true } });
    }, 1000);
  };

  const handleEditSubmit = () => {
    setIsEditModalOpen(false);
    setEditTenantData(null);
    fetchTenants();
  };

  const filteredTenants = tenants.filter(
    (tenant) =>
      (tenant.tenanT_NAME ?? "")
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      (tenant.tenanT_ABREVATION ?? "")
        .toLowerCase()
        .includes(searchText.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const columns = [
    {
      name: "Tenant Name",
      selector: (row: Tenant) => row.tenanT_NAME ?? "",
      sortable: true,
      width: "200px",
    },
    {
      name: "Abbreviation",
      selector: (row: Tenant) => row.tenanT_ABREVATION ?? "",
      sortable: true,
      width: "150px",
    },
    {
      name: "Phone",
      selector: (row: Tenant) => row.phone ?? "",
      sortable: true,
      width: "120px",
    },
    {
      name: "Email",
      selector: (row: Tenant) => row.email ?? "",
      sortable: true,
      width: "200px",
    },
    {
      name: "Address",
      selector: (row: Tenant) => row.addresS1 ?? "",
      sortable: true,
      width: "200px",
    },
    {
      name: "2nd Address",
      selector: (row: Tenant) => row.addresS2 ?? "",
      sortable: true,
      width: "200px",
    },
    {
      name: "Website",
      selector: (row: Tenant) => row.website ?? "",
      sortable: true,
      width: "200px",
    },
    {
      name: "Active",
      selector: (row: Tenant) => (row.isactive ? "Yes" : "No"),
      sortable: true,
      cell: (row: Tenant) => (row.isactive ? "Yes" : "No"),
      width: "100px",
    },
    {
      name: "Created On",
      selector: (row: Tenant) => row.createD_ON ?? "",
      sortable: true,
      cell: (row: Tenant) => formatDate(row.createD_ON),
      width: "130px",
    },
    {
      name: "",
      cell: (row: Tenant) => (
        <div className="action-cell">
          <button
            className="action-button edit-button"
            title="Edit Tenant"
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil-square"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "60px",
    },
  ];

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Tenant List</div>
                      <div>
                        <input
                          type="text"
                          placeholder="Search by Name or Abbreviation"
                          className="p-2 w-full datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "21rem" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsCreateModalOpen(true)}
                        >
                          +Add New Tenant
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredTenants}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        selectableRows
                        selectableRowsComponentProps={{
                          className: "custom-checkbox",
                        }}
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <CreateTenantModal
              isOpen={isCreateModalOpen}
              onClose={() => setIsCreateModalOpen(false)}
              onSubmit={handleCreateSubmit}
            />
            <EditTenantModal
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setEditTenantData(null);
              }}
              onSubmit={handleEditSubmit}
              tenantData={editTenantData}
            />
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={loading}
        message="Loading Tenant Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default TenantTable;
